import { createAsyncThunk, ThunkDispatch } from "@reduxjs/toolkit";
import { DerivState } from "./derivSlice";
import { actionReducerItems, dateInReloadRange } from "../common/commonActions";
import { AppThunk, RootState } from "../store";
import { loadSideMenuFromEvent } from "../schedule/scheduleActions";
import { rdsEvent_Dto } from "../../interfaces/dtos/rdsEvent";
import { apiGet } from "../../utils/api";
import { Deriv, DerivMapping } from "../../interfaces/dtos/deriv";
import { apiUrls } from "../../components/shared/apiUrls";
import { getDerivCalculated } from "./derivCalculated";
import { loadRdsEventStatuses } from "../rds/rdsActionsEventStatus";
import axios from "axios";
import { loadRdsSelections } from "../rds/rdsActionsSelection";
import { loadRdsRunner } from "../rds/rdsActionsRunner";
import { fileVersionAppendix, toUKDate } from "../../utils/commonUtils";

export const loadEventDerivs = (eventId: number, forReloading?: boolean): AppThunk =>
    async (dispatch, getState) => {

        const reloading = forReloading ?? false;

        await actionScheduleForItem(dispatch, getState().deriv, reloading, async () => {

            await dispatch(loadSideMenuFromEvent(eventId));
            const event = getState().rds.rdsEvents.find((e) => e.id === eventId);

            if (event && !mustReload(event, getState().deriv, reloading)) {
                return;
            }

            dispatch(loadRdsEventStatuses(eventId));  // Don't need await as can load syncronously
            await dispatch(loadRdsSelections(eventId));  // wait for selections to load as used below
            const selections = getState().rds.rdsSelections.filter((s) => s.eventId === eventId);
            const runnerFuncs = selections.map((s) => dispatch(loadRdsRunner({
                selectionId: s.id,
                selectionStatus: s.status
            })));
            const loadEventWinFunc = dispatch(loadEventDerivItem(eventId));
            await Promise.allSettled([...runnerFuncs, loadEventWinFunc]);
        })
    };

const mustReload = (event: rdsEvent_Dto, deriv: DerivState, forceReload: boolean) => {
    if (dateInReloadRange(event.dateTime)) {
        return true;
    }

    if (forceReload) {
        return true;
    }

    const eventDeriv = deriv.eventDerivs.filter((ed) => ed.eventId === event.id);
    if (eventDeriv.length === 0) {
        return true;
    }

    return false;
}

export const loadEventDerivItem = createAsyncThunk(
    'deriv/loadEventDerivItem',
    async (eventId: number, { getState, dispatch }) => {

        let { deriv } = getState() as RootState;

        const result = await apiGet<Deriv[]>(apiUrls.derivMarket.getAll(eventId));        

        const otherDerivEvents = deriv.eventDerivs.filter((ed) => ed.eventId !== eventId);
        const sortedDerivs = result.toSorted((a, b) => ((a.version > b.version ? 1 : -1)));
        await dispatch(loadDerivMappings());
        deriv = (getState() as RootState).deriv;

        return [...otherDerivEvents, ...mapDerivs(sortedDerivs, eventId, deriv.derivMappings)];
    }
);

const mapDerivs = (derivs: Deriv[], eventId: number, derivMappings: DerivMapping[]): Deriv[] => {
    return derivs.map((d) => mapDeriv(d, eventId, derivMappings));
}

const mapDeriv = (deriv: Deriv, eventId: number, derivMappings: DerivMapping[]): Deriv => {
    const timestamp = toUKDate(deriv.timestamp);
    return {
        ...deriv,
        eventId,
        timestamp,
        calculated: getDerivCalculated(deriv, derivMappings)
    }
}

// loadDerivMappings
export const loadDerivMappings = createAsyncThunk(
    'deriv/loadDerivMappings',
    async () => {
        const result =  await axios.get<DerivMapping[]>(`/derivMappings.json${fileVersionAppendix()}`);
        return result.data;
    },
    {
        condition: (_, { getState }) => {
            // If we already have deriv mappings then no need to get them again
            const rootState = getState() as RootState;
            if (!rootState) { return true; }
            if (rootState.deriv.loadingDerivMappings) { return false; }  // already loading
            return rootState.deriv.derivMappings.length === 0;
        }
    }
);

export const setLoading = createAsyncThunk(
    'deriv/setLoading',
    async (loading: boolean) => {
        return loading;
    }
);

export const setReloading = createAsyncThunk(
    'deriv/setReloading',
    async (loading: boolean) => {
        return loading;
    }
);

const actionScheduleForItem = async (
    // allow parameter to be any
    // eslint-disable-next-line
    dispatch: ThunkDispatch<any, any, any>,
    derivState: DerivState,
    reloading: boolean,
    process: () => Promise<void>
) => {
    await actionReducerItems(
        dispatch,
        reloading ? derivState.reloading : derivState.loading,
        reloading ? setReloading : setLoading,
        process
    );
}